import { useState, useEffect, useMemo } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, RefreshCw, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export function OneSheetLeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/one-sheet-leads");
      setLeads(r.data.leads || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate + "T00:00:00") : null;
    const to = toDate ? new Date(toDate + "T23:59:59") : null;
    return leads.filter((l) => {
      if (q && !`${l.name} ${l.email} ${l.phone}`.toLowerCase().includes(q)) return false;
      if (from || to) {
        const d = new Date(l.created_at);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [leads, query, fromDate, toDate]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Name", "Email", "Phone", "Requested At"];
    const rows = filtered.map((l) => [l.name, l.email, l.phone, l.created_at].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `one-sheet-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div data-testid="one-sheet-leads-panel">
      <OneSheetUploader />

      <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
        <p className="text-sm text-white/55">
          {filtered.length} of {leads.length} contact{leads.length === 1 ? "" : "s"} captured via the EPK one-sheet gate
        </p>
        <div className="flex items-center gap-3">
          <Button onClick={load} variant="ghost" className="rounded-none px-4 py-4 text-xs text-white/60 hover:text-[color:var(--gold)]" data-testid="leads-refresh">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button onClick={exportCsv} disabled={!filtered.length} className="btn-gold rounded-none px-6 py-4 text-xs" data-testid="leads-export-csv">
            <Download size={14} /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, phone…"
          className="sm:col-span-2 bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
          data-testid="leads-search"
        />
        <label className="flex items-center gap-2 text-xs text-white/45">
          From
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0" data-testid="leads-from" />
        </label>
        <label className="flex items-center gap-2 text-xs text-white/45">
          To
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0" data-testid="leads-to" />
        </label>
      </div>

      {loading ? (
        <p className="text-white/45 py-6">Loading leads…</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/45 py-6" data-testid="leads-empty">
          {leads.length === 0
            ? "No one-sheet requests yet. Leads appear here when press or promoters download the one-sheet from the EPK."
            : "No leads match your search/date filter."}
        </p>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
                <th className="px-5 py-4 font-medium">Name</th>
                <th className="px-5 py-4 font-medium">Email</th>
                <th className="px-5 py-4 font-medium">Phone</th>
                <th className="px-5 py-4 font-medium">Requested</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-4 text-white/85">{l.name}</td>
                  <td className="px-5 py-4">
                    <a href={`mailto:${l.email}`} className="text-[color:var(--gold)] hover:underline">{l.email}</a>
                  </td>
                  <td className="px-5 py-4 text-white/70">
                    <a href={`tel:${l.phone}`} className="hover:text-[color:var(--gold)]">{l.phone}</a>
                  </td>
                  <td className="px-5 py-4 text-white/50 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OneSheetUploader() {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/one-sheet");
      setMeta(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/one-sheet/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMeta(r.data);
      toast.success("One-sheet updated — print-ready PDF regenerated automatically.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="one-sheet-uploader">
      <img
        src={imgUrl((meta?.thumb_url) || "/api/static/press/one-sheet-thumb.jpg")}
        alt="Current one-sheet"
        className="w-28 rounded border border-white/10 shrink-0"
        data-testid="one-sheet-current-preview"
      />
      <div className="flex-1">
        <p className="font-serif text-xl tracking-tight">Artist One-Sheet</p>
        <p className="text-sm text-white/55 mt-1 max-w-lg">
          This is what press &amp; promoters download from the EPK. Upload a new image and the
          <strong className="text-white/75"> print-ready PDF is regenerated automatically</strong>.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <label className="btn-gold rounded-none inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] cursor-pointer" data-testid="one-sheet-upload-btn">
            <UploadCloud size={15} /> {busy ? "Uploading…" : "Upload New One-Sheet"}
            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
          </label>
          {meta?.pdf_url && (
            <a href={imgUrl(meta.pdf_url)} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em]" data-testid="one-sheet-view-pdf">
              <Download size={15} /> View PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
